/**
 * FBR Pakistan Digital Invoicing — Line-item mapper
 * Law-aligned, PRAL-safe, Annex-C compliant.
 * Handles ALL sale types and ensures reconciliation/audit integrity.
 */

import { UOM_NUMBERS_PIECES_UNITS } from "./constants";

// ---------------------------------------------------------------------------
// FBR Scenario IDs (SN001–SN028) — Sandbox / Production
// Each scenario has distinct validation and required fields.
// ---------------------------------------------------------------------------
export const FBR_SCENARIO_IDS = [
  "SN001", "SN002", "SN003", "SN004", "SN005", "SN006", "SN007", "SN008", "SN009",
  "SN010", "SN011", "SN012", "SN013", "SN014", "SN015", "SN016", "SN017", "SN018",
  "SN019", "SN020", "SN021", "SN022", "SN023", "SN024", "SN025", "SN026", "SN027", "SN028",
];

// Sale type → single scenarioId (when buyer registration does not change scenario)
const SALE_TYPE_TO_SCENARIO = {
  "Steel melting and re-rolling": "SN003",
  "Ship breaking": "SN004",
  "Goods at Reduced Rate": "SN005",
  "Exempt goods": "SN006",
  "Goods at zero-rate": "SN007",
  "3rd Schedule Goods": "SN008",
  "Cotton ginners": "SN009",
  "Telecommunication services": "SN010",
  "Toll Manufacturing": "SN011",
  "Petroleum Products": "SN012",
  "Electricity Supply to Retailers": "SN013",
  "Gas to CNG stations": "SN014",
  "Mobile Phones": "SN015",
  "Processing/Conversion of Goods": "SN016",
  "Goods (FED in ST Mode)": "SN017",
  "Services (FED in ST Mode)": "SN018",
  "Services": "SN019",
  "Electric Vehicle": "SN020",
  "Cement /Concrete Block": "SN021",
  "Potassium Chlorate": "SN022",
  "CNG": "SN023",
  "Goods as per SRO.297(I)/2023": "SN024",
  "Drugs at fixed ST (Eighth Schedule 81)": "SN025",
  "Goods at standard rate to end consumers (retailer)": "SN026",
  "3rd Schedule Goods to end consumers (retailer)": "SN027",
  "Goods at reduced rate to end consumers (retailer)": "SN028",
  // Non-Adjustable Supplies: no dedicated scenario; fall back to SN001/SN002 by buyer
};

// ---------------------------------------------------------------------------
// Sale types (canonical list; must match FBR/PRAL) — includes all SN001–SN028
// ---------------------------------------------------------------------------
export const FBR_SALE_TYPES = [
  "Goods at standard rate (default)",
  "Steel melting and re-rolling",
  "Ship breaking",
  "Goods at Reduced Rate",
  "Exempt goods",
  "Goods at zero-rate",
  "3rd Schedule Goods",
  "Cotton ginners",
  "Telecommunication services",
  "Toll Manufacturing",
  "Petroleum Products",
  "Electricity Supply to Retailers",
  "Gas to CNG stations",
  "CNG",
  "Mobile Phones",
  "Processing/Conversion of Goods",
  "Goods (FED in ST Mode)",
  "Services (FED in ST Mode)",
  "Services",
  "Electric Vehicle",
  "Cement /Concrete Block",
  "Potassium Chlorate",
  "Goods as per SRO.297(I)/2023",
  "Drugs at fixed ST (Eighth Schedule 81)",
  "Goods at standard rate to end consumers (retailer)",
  "3rd Schedule Goods to end consumers (retailer)",
  "Goods at reduced rate to end consumers (retailer)",
  "Non-Adjustable Supplies",
];

export const DEFAULT_SALE_TYPE = "Goods at standard rate (default)";

/**
 * Returns FBR scenarioId (SN001–SN028) for the given sale type and buyer registration.
 * SN001 = standard rate to Registered, SN002 = standard rate to Unregistered;
 * all other scenarios are determined by sale type.
 * @param {string} saleType - Sale type from form
 * @param {boolean} buyerRegistered - Whether buyer is sales tax registered
 * @returns {string} scenarioId e.g. "SN001", "SN006"
 */
export function getScenarioId(saleType, buyerRegistered) {
  const trimmed = (saleType && String(saleType).trim()) || DEFAULT_SALE_TYPE;
  const mapped = SALE_TYPE_TO_SCENARIO[trimmed];
  if (mapped) return mapped;
  // Standard rate (default) or Non-Adjustable Supplies: SN001 vs SN002 by buyer
  return buyerRegistered ? "SN001" : "SN002";
}

/** Sale types where FED (Federal Excise Duty) applies — amount in PKR, not %. */
const FED_APPLICABLE_SALE_TYPES = new Set([
  "Goods (FED in ST Mode)",
  "Services (FED in ST Mode)",
  "Cement /Concrete Block",
  "Mobile Phones",
  "Potassium Chlorate",
  "Petroleum Products",
  "Telecommunication services",
  "CNG",
]);

/** Sale types where SRO fields are typically required for audit (Sixth Schedule, reduced rate, etc.). */
const SRO_REQUIRED_SALE_TYPES = new Set([
  "Exempt goods",
  "Goods as per SRO.297(I)/2023",
  "Goods at Reduced Rate",
  "Goods at reduced rate to end consumers (retailer)",
  "3rd Schedule Goods",
  "3rd Schedule Goods to end consumers (retailer)",
  "Drugs at fixed ST (Eighth Schedule 81)",
]);

// ---------------------------------------------------------------------------
// Numeric safety (MANDATORY)
// ---------------------------------------------------------------------------

/**
 * Rounds to 2 decimals and ensures non-negative finite number.
 * No NaN, no undefined — safe for PRAL/FBR.
 */
export function round2(num) {
  const n = Number(num);
  if (!Number.isFinite(n)) return 0;
  return Number(Number(n).toFixed(2));
}

/**
 * Safe amount for monetary values: non-negative, rounded, no NaN/undefined.
 */
export function safeAmount(n) {
  return round2(Math.max(0, Number(n) || 0));
}

// ---------------------------------------------------------------------------
// Rate field logic (MANDATORY)
// Exempt → "Exempt", Zero-rated → "0%", else percentage
// ---------------------------------------------------------------------------

export function getRateLabel(saleType, taxRate) {
  if (saleType === "Exempt goods") return "Exempt";
  if (saleType === "Goods at zero-rate") return "0%";
  return `${Number(taxRate) || 0}%`;
}

// ---------------------------------------------------------------------------
// Sales tax calculation engine
// Exempt / Zero-rated → 0; otherwise discountedValue × taxRate
// ---------------------------------------------------------------------------

export function getSalesTaxApplicable(saleType, valueAfterDiscount, taxRate) {
  if (saleType === "Exempt goods" || saleType === "Goods at zero-rate") {
    return 0;
  }
  return round2((valueAfterDiscount * (Number(taxRate) || 0)) / 100);
}

// ---------------------------------------------------------------------------
// Withholding tax (Annex-C sensitive): only when buyer is withholding agent
// Must never exceed sales tax; must be amount, not %
// ---------------------------------------------------------------------------

export function getWithheldTax(isWithholdingApplicable, salesTaxApplicable, withholdingRatePercent) {
  if (!isWithholdingApplicable || salesTaxApplicable <= 0) return 0;
  const rate = Math.min(100, Math.max(0, Number(withholdingRatePercent) || 0));
  const withheld = round2((salesTaxApplicable * rate) / 100);
  return Math.min(withheld, salesTaxApplicable);
}

// ---------------------------------------------------------------------------
// FED: independent of sales tax; amount in PKR; apply only when sale type allows
// ---------------------------------------------------------------------------

export function getFedPayableForItem(saleType, invoiceFedAmount, itemFedOverride) {
  if (!FED_APPLICABLE_SALE_TYPES.has(saleType)) return 0;
  const amount = itemFedOverride != null ? itemFedOverride : invoiceFedAmount;
  return safeAmount(amount);
}

// ---------------------------------------------------------------------------
// Further tax: typically when buyer unregistered AND supply taxable
// Suppress for exempt, zero-rated, or when buyer is registered
// ---------------------------------------------------------------------------

export function getFurtherTaxForItem(saleType, buyerRegistered, furtherTaxAmount) {
  if (saleType === "Exempt goods" || saleType === "Goods at zero-rate") return 0;
  if (buyerRegistered) return 0;
  return safeAmount(furtherTaxAmount);
}

// ---------------------------------------------------------------------------
// Extra tax: only when item/category legally requires it; otherwise 0
// Never send empty string — must be numeric
// ---------------------------------------------------------------------------

export function getExtraTaxForItem(saleType, invoiceExtraAmount, itemExtraOverride) {
  // Apply only when sector/item requires; otherwise 0
  const amount = itemExtraOverride != null ? itemExtraOverride : invoiceExtraAmount;
  return safeAmount(amount ?? 0);
}

// ---------------------------------------------------------------------------
// Total values formula (PRAL STRICT) — Annex-C matching
// totalValues = valueAfterDiscount + salesTaxApplicable + fedPayable + extraTax + furtherTax - salesTaxWithheldAtSource
// ---------------------------------------------------------------------------

export function getTotalValues(valueAfterDiscount, salesTaxApplicable, fedPayable, extraTax, furtherTax, salesTaxWithheldAtSource) {
  const total =
    safeAmount(valueAfterDiscount) +
    safeAmount(salesTaxApplicable) +
    safeAmount(fedPayable) +
    safeAmount(extraTax) +
    safeAmount(furtherTax) -
    safeAmount(salesTaxWithheldAtSource);
  return Math.max(0, round2(total));
}

// ---------------------------------------------------------------------------
// Annex-C protection checks — log structured warnings if violated
// ---------------------------------------------------------------------------

export function validateAnnexCItem(item, saleType) {
  const warnings = [];
  if (saleType === "Exempt goods" && item.salesTaxApplicable !== 0) {
    warnings.push({ rule: "Exempt goods must have salesTaxApplicable = 0", value: item.salesTaxApplicable });
  }
  if (saleType === "Goods at zero-rate" && item.salesTaxApplicable !== 0) {
    warnings.push({ rule: "Zero-rated must have salesTaxApplicable = 0", value: item.salesTaxApplicable });
  }
  if (item.salesTaxWithheldAtSource > item.salesTaxApplicable) {
    warnings.push({
      rule: "withheld must be <= salesTaxApplicable",
      withheld: item.salesTaxWithheldAtSource,
      salesTax: item.salesTaxApplicable,
    });
  }
  if (item.totalValues < item.valueSalesExcludingST) {
    warnings.push({
      rule: "totalValues must be >= valueSalesExcludingST",
      totalValues: item.totalValues,
      valueSalesExcludingST: item.valueSalesExcludingST,
    });
  }
  if (item.quantity <= 0) {
    warnings.push({ rule: "quantity must be > 0", value: item.quantity });
  }
  if (item.rate === "Exempt" && item.salesTaxApplicable !== 0) {
    warnings.push({ rule: "Rate label Exempt requires sales tax 0", value: item.salesTaxApplicable });
  }
  if (item.rate === "0%" && item.salesTaxApplicable !== 0) {
    warnings.push({ rule: "Rate 0% requires sales tax 0", value: item.salesTaxApplicable });
  }
  if (warnings.length > 0 && typeof console !== "undefined" && console.warn) {
    console.warn("[FBR Annex-C] Item validation warnings:", { saleType, warnings, item });
  }
  return warnings;
}

// ---------------------------------------------------------------------------
// Main mapper: cart item → FBR Digital Invoicing line item
// ---------------------------------------------------------------------------

/**
 * @typedef {Object} FBRMapperContext
 * @property {number} invoiceDiscountAmount - Total invoice-level discount to prorate
 * @property {number} subTotal - Subtotal (for proration denominator)
 * @property {string} saleType - Sale type (e.g. from form)
 * @property {boolean} buyerRegistered - Buyer is registered (salesTaxRegistration === "Registered")
 * @property {boolean} [isWithholdingApplicable=false] - Buyer is withholding agent
 * @property {number} [withholdingRate=0] - Withholding rate (0-100)
 * @property {number} [fedPayable=0] - Invoice-level FED amount (PKR)
 * @property {number} [extraTax=0] - Invoice-level extra tax (when applicable)
 * @property {number} [furtherTax=0] - Further tax amount (when buyer unregistered)
 * @property {string} [defaultUom] - Default UoM for items without uom
 */

/**
 * Maps a single cart item to one FBR Digital Invoicing line item.
 * @param {Object} item - Cart item { quantity, price, taxRate, title, hscode, uom, discount?, sroItemSerialNo?, sroScheduleNo?, fedPayable?, withholdingRate? }
 * @param {FBRMapperContext} context
 * @returns {Object} FBR line item for DI API
 */
export function mapCartItemToFBRLineItem(item, context) {
  const {
    invoiceDiscountAmount = 0,
    subTotal = 1,
    saleType = DEFAULT_SALE_TYPE,
    buyerRegistered = false,
    isWithholdingApplicable = false,
    withholdingRate = 0,
    fedPayable = 0,
    extraTax = 0,
    furtherTax = 0,
    defaultUom = UOM_NUMBERS_PIECES_UNITS,
  } = context;

  const quantity = Math.max(0, Number(item.quantity) || 0);
  const unitPrice = Number(item.price) || 0;
  const taxRate = Number(item.taxRate) || 0;
  const saleValue = quantity * unitPrice;

  // Discount proration: proportional, cannot exceed line value, rounded
  const diSubTotal = subTotal > 0 ? subTotal : 1;
  const itemDiscount =
    invoiceDiscountAmount > 0 && diSubTotal > 0
      ? round2((saleValue / diSubTotal) * invoiceDiscountAmount)
      : 0;
  const itemDiscountCapped = Math.min(itemDiscount, saleValue);
  const valueAfterDiscount = Math.max(0, round2(saleValue - itemDiscountCapped));

  // Rate label: Exempt | 0% | N%
  const rateLabel = getRateLabel(saleType, taxRate);

  // Sales tax: 0 for exempt/zero-rated, else valueAfterDiscount × taxRate
  const salesTaxApplicable = getSalesTaxApplicable(saleType, valueAfterDiscount, taxRate);

  // Withholding: only when applicable; must not exceed sales tax
  const withheldTax = getWithheldTax(
    isWithholdingApplicable,
    salesTaxApplicable,
    item.withholdingRate ?? withholdingRate
  );

  const fedPayableItem = getFedPayableForItem(saleType, fedPayable, item.fedPayable);
  const extraTaxItem = getExtraTaxForItem(saleType, extraTax, item.extraTax);
  const furtherTaxItem = getFurtherTaxForItem(saleType, buyerRegistered, furtherTax);

  // PRAL strict total
  const totalValues = getTotalValues(
    valueAfterDiscount,
    salesTaxApplicable,
    fedPayableItem,
    extraTaxItem,
    furtherTaxItem,
    withheldTax
  );

  const fbrItem = {
    hsCode: String(item.hscode ?? item.hsCode ?? "").trim() || "",
    productDescription: String(item.title ?? item.productDescription ?? "Product").trim(),
    rate: rateLabel,
    uoM: String(item.uom ?? defaultUom).trim() || defaultUom,
    quantity,
    valueSalesExcludingST: safeAmount(valueAfterDiscount),
    fixedNotifiedValueOrRetailPrice: 0,
    salesTaxApplicable: safeAmount(salesTaxApplicable),
    salesTaxWithheldAtSource: safeAmount(withheldTax),
    totalValues: safeAmount(totalValues),
    saleType: saleType || DEFAULT_SALE_TYPE,
    discount: safeAmount(itemDiscountCapped),
    fedPayable: safeAmount(fedPayableItem),
    extraTax: safeAmount(extraTaxItem),
    furtherTax: safeAmount(furtherTaxItem),
    sroItemSerialNo: String(item.sroItemSerialNo ?? "").trim() || "",
    sroScheduleNo: String(item.sroScheduleNo ?? "").trim() || "",
  };

  // SRO validation warning: if sale type requires SRO and values missing
  if (SRO_REQUIRED_SALE_TYPES.has(saleType)) {
    if (!fbrItem.sroItemSerialNo && !fbrItem.sroScheduleNo && typeof console !== "undefined" && console.warn) {
      console.warn("[FBR SRO] Sale type may require SRO fields:", saleType, { sroItemSerialNo: fbrItem.sroItemSerialNo, sroScheduleNo: fbrItem.sroScheduleNo });
    }
  }

  validateAnnexCItem(fbrItem, saleType);

  return fbrItem;
}

/**
 * Maps full cart to FBR Digital Invoicing items array.
 * @param {Array} cartItems - Cart items
 * @param {FBRMapperContext} context
 * @returns {Array} FBR line items for DI API
 */
export function mapCartItemsToFBRDigitalInvoiceItems(cartItems, context) {
  if (!Array.isArray(cartItems)) return [];
  return cartItems
    .filter((item) => (Number(item.quantity) || 0) > 0)
    .map((item) => mapCartItemToFBRLineItem(item, context));
}
